import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock3, MessageCircleHeart, ArrowDown } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINES = [
  { text: "Parliamone,", cls: "text-ivory" },
  { text: "senza tabù,", cls: "text-ivory" },
  { text: "senza paure.", cls: "italic text-gold" },
];

const CHIPS = [
  { icon: ShieldCheck, label: "Comfort del paziente" },
  { icon: Clock3, label: "Recupero rapido quando appropriato" },
  { icon: MessageCircleHeart, label: "Senza tabù" },
];

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 55]);
  const yGlow = useTransform(scrollY, [0, 600], [0, -50]);

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <motion.div
        style={{ y: yGlow }}
        className="absolute -top-48 -left-40 w-[620px] h-[620px] rounded-full bg-gold/10 blur-[150px]"
      />
      <div className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full bg-sage/20 blur-[150px]" />

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="eyebrow"
          >
            Colonproctologia · Oltre 30 anni di esperienza
          </motion.p>

          <h1 className="font-display text-[2.9rem] sm:text-6xl lg:text-7xl leading-[1.06] mt-6 tracking-tight">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.28 + i * 0.14, ease: EASE }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 text-lg sm:text-xl text-silver font-light leading-relaxed max-w-xl"
          >
            Sono il <span className="text-ivory font-medium">Dott. Roberto Di Lorenzo</span>. Tratto
            emorroidi, ragadi, fistole perianali, ascessi, cisti pilonidale, diverticolosi e
            prolasso rettale, con attenzione al comfort del paziente e diverse possibilità
            terapeutiche, valutate caso per caso. Quando appropriato, privilegio tecniche orientate
            a favorire il recupero rapido: i tempi dipendono dal trattamento e solitamente sono
            molto rapidi.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#prenota"
              data-testid="hero-book-button"
              className="group flex items-center gap-2 bg-gold text-ink font-semibold uppercase tracking-wide rounded-full px-7 py-3.5 hover:bg-goldlight transition-all hover:shadow-[0_0_36px_rgba(212,163,89,0.3)]"
            >
              Prenota a Palermo
              <ArrowDown size={17} className="transition-transform group-hover:translate-y-0.5" />
            </a>
            <a
              href="#secondaopinione"
              data-testid="hero-second-opinion-button"
              className="text-ivory/90 border border-white/15 rounded-full px-7 py-3.5 uppercase tracking-wide hover:border-gold/50 hover:text-gold transition-all"
            >
              Informazioni seconda opinione online
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {CHIPS.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-silver border border-white/10 rounded-full px-4 py-2"
              >
                <Icon size={14} className="text-gold" /> {label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative mt-8 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative max-w-[460px] mx-auto lg:mx-0 lg:ml-auto"
          >
            <div className="absolute -inset-3 rounded-[2.2rem] border border-gold/20 translate-x-4 translate-y-4" />
            <div className="relative rounded-[2rem] overflow-hidden border border-gold/25 shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_proctologia-roberto/artifacts/701bdabddbc48d82_IMG_6503.webp"
                alt="Dott. Roberto Di Lorenzo nel suo studio di colonproctologia"
                className="w-full h-[460px] sm:h-[560px] object-cover object-top"
              />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink via-ink/70 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-display text-2xl text-white">Dott. Roberto Di Lorenzo</p>
                <p className="text-sm text-white/80 mt-1">Specialista in Colonproctologia · 30+ anni</p>
              </div>
            </div>

            <div className="animate-floaty absolute -left-6 sm:-left-10 top-10 bg-elevated/90 backdrop-blur border border-gold/25 rounded-2xl px-4 py-3 shadow-xl">
              <p className="font-mono-accent text-2xl text-gold leading-none">30+</p>
              <p className="text-[11px] text-silver mt-1">anni di esperienza</p>
            </div>
            <div className="animate-floaty absolute -right-3 sm:-right-8 bottom-16 bg-elevated/90 backdrop-blur border border-sagelight/30 rounded-2xl px-4 py-3 shadow-xl" style={{ animationDelay: "1.4s" }}>
              <p className="font-mono-accent text-2xl text-sagelight leading-none">Comfort</p>
              <p className="text-[11px] text-silver mt-1">al centro di tutto</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#trattamenti"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-silver/60 hover:text-gold transition-colors"
        data-testid="hero-scroll-hint"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scorri</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
